import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { Notification } from '@/types';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '@/lib/dateLocale';
import { Heart, MessageCircle, UserPlus, Info, Bell, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';

interface NotificationItemProps {
    notification: Notification;
    onClick: (id: string, link?: string) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {

    const getInitials = (name?: string) => (name || 'S').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    const getIcon = () => {
        switch (notification.type) {
            case 'like': return <Heart className="h-3 w-3 text-red-500 fill-current" />;
            case 'comment': return <MessageCircle className="h-3 w-3 text-blue-500 fill-current" />;
            case 'follow': return <UserPlus className="h-3 w-3 text-green-500" />;
            case 'achievement': return <Trophy className="h-3 w-3 text-amber-500" />;
            case 'system': return <Info className="h-3 w-3 text-slate-500" />;
            default: return <Bell className="h-3 w-3 text-slate-500" />;
        }
    };

    return (
        <DropdownMenuItem
            className={cn(
                "flex items-start gap-3 p-3 cursor-pointer focus:bg-accent",
                !notification.isRead && "bg-muted/40"
            )}
            onClick={() => onClick(notification.id, notification.link)}
        >
            <div className="relative mt-1">
                <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={notification.metadata?.actorPhotoURL} />
                    <AvatarFallback>{getInitials(notification.metadata?.actorName)}</AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -end-1 bg-white dark:bg-zinc-950 rounded-full p-0.5 shadow-sm border border-border">
                    {getIcon()}
                </div>
            </div>

            <div className="flex-1 space-y-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <p className={cn("text-sm truncate", !notification.isRead && "font-semibold")}>
                        {notification.title}
                    </p>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {formatDistanceToNow(notification.createdAt, { addSuffix: true, locale: getDateFnsLocale() })}
                    </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                    {notification.message}
                </p>
                {!notification.isRead && (
                    <div className="flex justify-end mt-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                    </div>
                )}
            </div>
        </DropdownMenuItem>
    );
};
